class SideNav {
  /**
   * Event handler to block the clicks.
   *
   * @param {Event} event - click event.
   * */
  static blockClicks(event) {
    event.stopPropagation();
  }

  constructor() {
    // Initialise attributes
    this.sideNavElement = document.querySelector('.side-nav');
    this.sideNavContainerElement = document.querySelector('.side-nav__container');
    this.toggleButtonElement = document.querySelector('.navbar__toggle');
    this.toggleButtonIconElement = document.querySelector('.navbar__toggle svg use');
    this.sideNavCollapseElements = document.querySelectorAll('.side-nav__container a, .side-nav__container button');

    this.startX = 0;
    this.currentX = 0;
    this.touchingSideNav = false;

    this.transitionEndProperty = null;
    this.transitionEndTime = 0;

    this.supportsPassive = undefined;

    // Bind functions
    this.showSideNav = this.showSideNav.bind(this);
    this.hideSideNav = this.hideSideNav.bind(this);
    this.toggleSideNav = this.toggleSideNav.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.update = this.update.bind(this);
    this.onTransitionEnd = this.onTransitionEnd.bind(this);

    this._setup();
  }

  // Event Handlers

  /**
   * Event handler to show the sideNav.
   *
   * @param {Event} event - click event.
   * */
  showSideNav(event) {
    event.preventDefault();

    this.sideNavElement.classList.add('side-nav--animatable');
    this.sideNavElement.classList.add('side-nav--visible');
    this.toggleButtonIconElement.setAttribute('xlink:href', '#icon-close');

    this.transitionEndProperty = 'transform';
    // the duration of transition (make unique to distinguish transitions )
    this.transitionEndTime = 0.33;

    this.sideNavElement.addEventListener('transitionend', this.onTransitionEnd);
  }

  /**
   * Event handler to hide the sideNav.
   *
   * @param {Event} event - click event.
   * */
  hideSideNav(event) {
    this.sideNavElement.classList.add('side-nav--animatable');
    this.sideNavElement.classList.remove('side-nav--visible');
    this.toggleButtonIconElement.setAttribute('xlink:href', '#icon-menu');

    this.transitionEndProperty = 'transform';
    this.transitionEndTime = 0.13;

    this.sideNavElement.addEventListener('transitionend', this.onTransitionEnd);
  }

  /**
   * Event handler to toggle the sideNav.
   *
   * @param {Event} event - click event.
   * */
  toggleSideNav(event) {
    event.preventDefault();

    let classAdded = this.sideNavElement.classList.toggle('side-nav--visible');

    if (classAdded) {
      this.toggleButtonIconElement.setAttribute('xlink:href', '#icon-close');
    } else {
      this.toggleButtonIconElement.setAttribute('xlink:href', '#icon-menu');
    }
  }

  /**
   * Event handler for touchstart event.
   *
   * @param {Event} event - click event.
   * */
  onTouchStart(event) {
    if (!this.sideNavElement.classList.contains('side-nav--visible')) { return; }

    this.startX = event.touches[0].pageX;
    this.currentX = this.startX;
    this.touchingSideNav = true;

    requestAnimationFrame(this.update);
  }

  /**
   * Event handler for touchmove event.
   *
   * @param {Event} event - click event.
   * */
  onTouchMove(event) {
    if (!this.touchingSideNav) { return; }

    this.currentX = event.touches[0].pageX;
  }

  /**
   * Event handler for touchend event.
   *
   * @param {Event} event - click event.
   * */
  onTouchEnd(event) {
    if (!this.touchingSideNav) { return; }

    this.touchingSideNav = false;

    const translateX = Math.min(0, this.currentX - this.startX);
    this.sideNavContainerElement.style.transform = '';

    if (translateX < 0) { this.hideSideNav(event); }
  }

  /**
   * Event handler for end of transition of the sideNav.
   *
   * @param {Event} event
   * */
  onTransitionEnd(event) {
    if (event.propertyName !== this.transitionEndProperty && event.elapsedTime !== this.transitionEndTime) { return; }

    this.transitionEndProperty = null;
    this.transitionEndTime = 0;

    this.sideNavElement.classList.remove('side-nav--animatable');
    this.sideNavElement.removeEventListener('transitionend', this.onTransitionEnd);
  }

  // private

  _setup() {
    this._addEventListeners();
  }

  /**
   * Adds all the required event listeners.
   * */
  _addEventListeners() {
    this.toggleButtonElement && this.toggleButtonElement.addEventListener('click', this.toggleSideNav);
    this.sideNavElement.addEventListener('click', this.hideSideNav);
    this.sideNavContainerElement.addEventListener('click', this.constructor.blockClicks);
    this.sideNavCollapseElements.forEach(element => {
      element.addEventListener('click', this.hideSideNav);
    });

    this.sideNavContainerElement.addEventListener('touchstart', this.onTouchStart);
    this.sideNavContainerElement.addEventListener('touchmove', this.onTouchMove);
    this.sideNavContainerElement.addEventListener('touchend', this.onTouchEnd);
  }

  /**
   * Renders the sideNav based the touch action.
   * */
  update() {
    if (!this.touchingSideNav) { return; }

    requestAnimationFrame(this.update);

    const translateX = Math.min(0, this.currentX - this.startX);
    this.sideNavContainerElement.style.transform = `translateX(${translateX}px)`;
  }
}

export default SideNav;
